<template>
  <div class="historico-integracao-drawer">
    <PDrawer
      v-model="visibleDrawer"
      width="1180px"
      :title="drawerTitle"
      @close="handleClose"
      :close-on-click-modal="false"
      small-title
    >
      <div class="historico-integracao-drawer__content">
        <BannerInformativoHistoricoIntegracoes />

        <div class="historico-integracao-drawer__content-header">
          <PDatePicker
            v-model="historyDateRef"
            format="DD/MM/YYYY"
            :clearable="false"
            label="Data do histórico"
            placeholder="Selecione uma data"
            type="date"
            @change="handleChangeDate"
          />
          <PButton
            style="margin-bottom: 4px"
            prefix-icon="refresh"
            type="link"
            @click="getIntegrationHistory"
            :loading="isLoading"
            >Atualizar</PButton
          >
        </div>
        <div class="historico-integracao-drawer__workspace">
          <PTable
            :data="integrationHistory ?? []"
            row-key="id"
            class="historico-integracao-drawer__table"
            :loading="isLoading"
            :pagination="true"
            :page="paginationRef.page"
            :quantidade-por-pagina="paginationRef.quantidadePorPagina"
            :total-pages="paginationRef.totalPages"
            :expand-row-keys="expandedKeys"
            @expand-change="handleExpandChange"
            @pagination="handlePagination"
            @linhas="handleRows"
          >
            <PTableColumn
              label="Detalhes"
              width="40px"
              prop="hasDetailLog"
              align="center"
              type="expand"
            >
              <template #expand="{ expanded }">
                <div>
                  <PMaterialIcon
                    :icon="
                      expanded ? 'keyboard_arrow_down' : 'keyboard_arrow_right'
                    "
                    size="22px"
                    :style="{
                      color: expanded ? '#0055ff' : '#6d6d6d'
                    }"
                  />
                </div>
              </template>
              <template #default="{ row }">
                <div
                  class="historico-integracao-drawer__detalhes"
                  v-if="isExpandableRow(row)"
                >
                  <template v-if="getRowSection(row) === 'info'">
                    <p
                      v-if="row?.Status"
                      class="historico-integracao-drawer__status"
                    >
                      {{ row.Status }}
                    </p>
                    <span
                      v-if="getDetailLog(row)"
                      class="historico-integracao-drawer__log"
                      >{{ getDetailLog(row) }}</span
                    >
                    <div
                      v-if="!row?.Status && !getDetailLog(row)"
                      class="historico-integracao-drawer__detalhe-bloco"
                    >
                      <span class="historico-integracao-drawer__detalhe-vazio">
                        Sem informações da solicitação.
                      </span>
                    </div>
                  </template>

                  <div
                    v-else-if="getRowSection(row) === 'files'"
                    class="historico-integracao-drawer__detalhe-bloco"
                  >
                    <div class="historico-integracao-drawer__detalhe-titulo">
                      <PMaterialIcon icon="folder_zip" size="16px" />
                      <strong>Arquivos</strong>
                    </div>

                    <div
                      v-if="isLoadingFiles(row)"
                      class="historico-integracao-drawer__detalhe-vazio"
                    >
                      Carregando arquivos...
                    </div>

                    <template v-else>
                      <ul
                        v-if="getRowFiles(row).length"
                        class="historico-integracao-drawer__file-list"
                      >
                        <li
                          v-for="file in getRowFiles(row)"
                          :key="file._id"
                          class="historico-integracao-drawer__file-item"
                        >
                          <strong>{{ file.Nome || "Arquivo" }}</strong>
                          <span>{{ file._id }}</span>
                        </li>
                      </ul>

                      <div
                        v-else
                        class="historico-integracao-drawer__detalhe-vazio"
                      >
                        Nenhum arquivo encontrado para este histórico.
                      </div>

                      <PButton
                        v-if="getRowFiles(row).length"
                        type="link"
                        prefix-icon="download"
                        :loading="isDownloadingFiles(row)"
                        @click="handleDownloadFiles(row)"
                      >
                        Baixar arquivos
                      </PButton>
                    </template>
                  </div>

                  <div
                    v-else-if="getRowSection(row) === 'curls'"
                    class="historico-integracao-drawer__detalhe-bloco"
                  >
                    <div class="historico-integracao-drawer__detalhe-titulo">
                      <PMaterialIcon icon="link" size="16px" />
                      <strong>Dados técnicos</strong>
                      <PButton
                        type="link"
                        prefix-icon="content_copy"
                        @click="copyAllCurls(row)"
                      >
                        Copiar todos
                      </PButton>
                    </div>

                    <ul class="historico-integracao-drawer__curl-list">
                      <li
                        v-for="(curl, index) in row.Curls"
                        :key="`${row.id}-curl-${index}`"
                        class="historico-integracao-drawer__curl-item"
                        :title="curl"
                      >
                        <span class="historico-integracao-drawer__curl-index"
                          >#{{ index + 1 }}</span
                        >
                        <code class="historico-integracao-drawer__curl-text">{{
                          curl
                        }}</code>
                        <button
                          type="button"
                          class="historico-integracao-drawer__icon-button"
                          title="Copiar cURL"
                          @click="copyCurl(curl)"
                        >
                          <PMaterialIcon icon="content_copy" size="16px" />
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </template>
            </PTableColumn>
            <PTableColumn label="Usuário" prop="Usuario">
              <template #default="{ row }">
                {{ row.Usuario }}
              </template>
            </PTableColumn>
            <PTableColumn label="Período Solicitado" prop="DataPesquisada">
              <template #default="{ row }">
                <template v-if="row.DataPesquisadaFim">
                  <div>
                    Início: {{ formatPeriodoSolicitado(row.DataPesquisada) }}
                  </div>
                  <div>
                    Fim: {{ formatPeriodoSolicitado(row.DataPesquisadaFim) }}
                  </div>
                </template>
                <div v-else>
                  {{ formatPeriodoSolicitado(row.DataPesquisada) }}
                </div>
              </template>
            </PTableColumn>
            <PTableColumn label="Processamento" prop="StatusProcessamento">
              <template #default="{ row }">
                <div>
                  Início: {{ dateLib.format(row.Inicio, EDateFormat.dayHour) }}
                </div>
                <div>
                  Fim: {{ formatProcessingEnd(row.Fim) }}
                </div>
              </template>
            </PTableColumn>
            <PTableColumn label="Status" :prop="historyStatusColumnProp" />
            <PTableColumn label="Arquivos" prop="Arquivos" align="center">
              <template #default="{ row }">
                <span
                  :title="getFilesTitle(row)"
                  :style="getFilesStyle(row)"
                  style="
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    justify-content: center;
                  "
                  @click="handleToggleFiles(row)"
                >
                  <PMaterialIcon
                    icon="folder_zip"
                    size="16px"
                    :style="getFileIconStyle(row)"
                  />
                </span>
              </template>
            </PTableColumn>
            <PTableColumn
              v-if="hasAnyCurls"
              label="Dados tecnicos"
              prop="Curls"
              width="140px"
              align="center"
            >
              <template #default="{ row }">
                <span
                  :style="getCurlsStyle(row)"
                  style="
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    justify-content: center;
                  "
                  @click="handleToggleCurls(row)"
                >
                  cURL

                  <PMaterialIcon
                    icon="link"
                    size="16px"
                    :style="getCurlsIconStyle(row)"
                  />
                </span>
              </template>
            </PTableColumn>
          </PTable>
        </div>
      </div>
    </PDrawer>
  </div>
</template>

<script setup lang="ts">
import {
  PButton,
  PDatePicker,
  PDrawer,
  PMaterialIcon,
  PTable,
  PTableColumn
} from "@f360-dev/f360-prisma";
import {
  PainelDeIntegracaoesGateway,
  TArquivoDetalhesIntegracao,
  THistoricoIntegracao,
  TIntegrationHistoryFile,
  TWebservice
} from "../../services/PainelDeIntegracaoesGateway";
import useHistoricoIntegracao from "./useHistoricoIntegracao";
import { formatPeriodoSolicitado } from "./formatPeriodoSolicitado";
import { computed, onMounted, ref } from "vue";
import { diContainer } from "@/inversify.config";
import { IToastNotification } from "@/core/composables/notification/types";
import {
  DITypes,
  EDateFormat,
  IUserProfile,
  LocalStorageKeys,
  TPagination
} from "@/core/@types/types";
import BannerInformativoHistoricoIntegracoes from "./BannerInformativoHistoricoIntegracoes.vue";
import useStorage from "@/core/plugins/useStorage";
import useDate from "@/core/plugins/useDate";

const historyDateRef = ref<string>(new Date().toISOString());

type THistoricoSection = "info" | "files" | "curls";

const isLoading = ref(false);
const expandedKeys = ref<string[]>([]);
const sectionByRow = ref<Record<string, THistoricoSection>>({});
const filesByRow = ref<Record<string, TIntegrationHistoryFile[]>>({});
const loadingFilesKeys = ref<string[]>([]);
const downloadingKeys = ref<string[]>([]);
const props = defineProps<{
  webservice: TWebservice;
  painelIntegracoesGateway: PainelDeIntegracaoesGateway;
}>();

const drawerTitle = computed(() => {
  const integrationName =
    props.webservice?.integracao ??
    props.webservice?.nome ??
    props.webservice?.Webservice;

  return integrationName
    ? `Histórico da integração | ${integrationName}`
    : "Histórico de Integração";
});

const hasFiles = (row: THistoricoIntegracao) =>
  (row?.Arquivos?.length ?? 0) > 0 || !!row?.ArquivoId;

const hasCurls = (row: THistoricoIntegracao) =>
  row?.Curls?.some(
    (curl) => typeof curl === "string" && curl.trim().length > 0
  ) ?? false;

const historyStatusColumnProp = "StatusProcessamento";

const formatProcessingEnd = (end?: string) =>
  end ? dateLib.format(end, EDateFormat.dayHour) : "---";

const { visibleDrawer, historicoIntegracao: integrationHistory } =
  useHistoricoIntegracao();

const hasAnyCurls = computed(() =>
  (integrationHistory.value ?? []).some((row) => hasCurls(row))
);

const isExpandableRow = (row: THistoricoIntegracao) =>
  !!row?.Status ||
  (row?.LogDetalhado?.length ?? 0) > 0 ||
  hasFiles(row) ||
  hasCurls(row);

const getDetailLog = (row: THistoricoIntegracao) =>
  row?.LogDetalhado?.map((log) => log.Message).join("\n") ?? "";

const getFilesTitle = (row: THistoricoIntegracao) =>
  row?.Arquivos?.map((file) => file.Nome).join(", ") ??
  (row?.ArquivoId ? "Ver detalhes do arquivo" : "");

const getFilesStyle = (row: THistoricoIntegracao) => ({
  color: hasFiles(row) ? "#0055ff" : "#6d6d6d",
  cursor: hasFiles(row) ? "pointer" : "default"
});

const getFileIconStyle = (row: THistoricoIntegracao) => ({
  color: hasFiles(row) ? "#0055ff" : "#6d6d6d"
});

const getCurlsStyle = (row: THistoricoIntegracao) => ({
  color: hasCurls(row) ? "#0055ff" : "#6d6d6d",
  cursor: hasCurls(row) ? "pointer" : "default"
});

const getCurlsIconStyle = (row: THistoricoIntegracao) => ({
  color: hasCurls(row) ? "#0055ff" : "#6d6d6d"
});

const isLoadingFiles = (row: THistoricoIntegracao) =>
  loadingFilesKeys.value.includes(row.id);

const isDownloadingFiles = (row: THistoricoIntegracao) =>
  downloadingKeys.value.includes(row.id);

const getRowFiles = (row: THistoricoIntegracao): TIntegrationHistoryFile[] =>
  row.Arquivos?.length ? row.Arquivos : filesByRow.value[row.id] ?? [];

const normalizeFilesFromDetails = (
  row: THistoricoIntegracao,
  details?: TArquivoDetalhesIntegracao
): TIntegrationHistoryFile[] => {
  if (row.Arquivos?.length) return row.Arquivos;

  if (details?.Arquivos?.length) return details.Arquivos;

  const arquivoId = details?._id ?? row.ArquivoId;
  if (!arquivoId) return [];

  return [
    {
      _id: arquivoId,
      Nome: details?.Nome ?? "Arquivo retornado pelo webservice"
    }
  ];
};

const getRowSection = (row: THistoricoIntegracao): THistoricoSection =>
  sectionByRow.value[row.id] ?? "info";

const getDefaultRowSection = (row: THistoricoIntegracao): THistoricoSection => {
  if (row?.Status || getDetailLog(row)) return "info";
  if (hasFiles(row)) return "files";
  if (hasCurls(row)) return "curls";

  return "info";
};

const isRowExpanded = (row: THistoricoIntegracao) =>
  expandedKeys.value.includes(row.id);

const setRowSection = (row: THistoricoIntegracao, section: THistoricoSection) => {
  sectionByRow.value = { ...sectionByRow.value, [row.id]: section };
};

const openRowSection = (row: THistoricoIntegracao, section: THistoricoSection) => {
  setRowSection(row, section);

  if (!isRowExpanded(row)) {
    expandedKeys.value = [...expandedKeys.value, row.id];
  }
};

const expandHistoryRows = () => {
  const rows = integrationHistory.value ?? [];
  const expandableRows = rows.filter(isExpandableRow);
  const sections: Record<string, THistoricoSection> = {};

  expandableRows.forEach((row) => {
    sections[row.id] = sectionByRow.value[row.id] ?? getDefaultRowSection(row);
  });

  expandedKeys.value = expandableRows.map((row) => row.id);
  sectionByRow.value = sections;
};

const handleExpandChange = (
  row: THistoricoIntegracao,
  _expandedRows: THistoricoIntegracao[]
) => {
  expandHistoryRows();

  if (getRowSection(row) === "files") loadRowFiles(row);
};

const loadRowFiles = async (row: THistoricoIntegracao) => {
  if (!row.ArquivoId || row.Arquivos?.length) return;
  if (filesByRow.value[row.id] || isLoadingFiles(row)) return;

  try {
    loadingFilesKeys.value = [...loadingFilesKeys.value, row.id];
    const response =
      await props.painelIntegracoesGateway.obterDetalhesArquivoIntegracao(
        row.ArquivoId
      );

    if (!response?.Ok) {
      notification.showNotification("Erro ao obter detalhes do arquivo", {
        type: "error",
        timeout: 3000
      });

      return;
    }

    filesByRow.value = {
      ...filesByRow.value,
      [row.id]: normalizeFilesFromDetails(row, response.Result)
    };
  } finally {
    loadingFilesKeys.value = loadingFilesKeys.value.filter(
      (key) => key !== row.id
    );
  }
};

const handleToggleFiles = (row: THistoricoIntegracao) => {
  if (!hasFiles(row)) return;

  openRowSection(row, "files");
  loadRowFiles(row);
};

const handleToggleCurls = (row: THistoricoIntegracao) => {
  if (!hasCurls(row)) return;

  openRowSection(row, "curls");
};

const handleDownloadFiles = async (row: THistoricoIntegracao) => {
  const fileIds = getRowFiles(row)
    .map((file) => file._id)
    .filter(Boolean);

  if (!fileIds.length) return;

  try {
    downloadingKeys.value = [...downloadingKeys.value, row.id];
    await props.painelIntegracoesGateway.baixarArquivosIntegracao(fileIds);
  } catch {
    notification.showNotification("Não foi possível baixar os arquivos.", {
      type: "error"
    });
  } finally {
    downloadingKeys.value = downloadingKeys.value.filter(
      (key) => key !== row.id
    );
  }
};

const copyText = async (text: string) => {
  await navigator.clipboard.writeText(text);
  notification.showNotification("Conteudo copiado", {
    type: "success",
    timeout: 2000
  });
};

const copyCurl = async (curl: string) => {
  await copyText(curl);
};

const copyAllCurls = async (row: THistoricoIntegracao) => {
  await copyText((row.Curls ?? []).join("\n\n"));
};

const paginationRef = ref<TPagination>({
  page: 1,
  totalPages: 1,
  quantidadePorPagina: 5,
  totalItems: 0
});

const storage = useStorage();
const userProfile = storage.get<IUserProfile>(LocalStorageKeys.UserProfile);

const emits = defineEmits<{
  (e: "close"): void;
}>();

const notification = diContainer.get<IToastNotification<string>>(
  DITypes.NOTIFICATION
);

const dateLib = useDate();

const handleChangeDate = () => {
  getIntegrationHistory();
};

const resetExpandState = () => {
  expandedKeys.value = [];
  sectionByRow.value = {};
  filesByRow.value = {};
  loadingFilesKeys.value = [];
  downloadingKeys.value = [];
};

const getIntegrationHistory = async () => {
  try {
    isLoading.value = true;
    resetExpandState();

    const response =
      await props.painelIntegracoesGateway.obterHistoricoIntegracao({
        serviceId: props.webservice.id ?? props.webservice._id ?? "",
        data: historyDateRef.value,
        client_id: userProfile?._id ?? "",
        page: paginationRef.value.page,
        pageSize: paginationRef.value.quantidadePorPagina
      });

    if (!response?.Ok) {
      notification.showNotification("Erro ao obter histórico de integração", {
        type: "error",
        timeout: 3000
      });

      return;
    }

    integrationHistory.value =
      response.Result?.map((item, index) => ({
        id: `historico-integracao-${index}-${item.DataPesquisada ?? ""}`,
        hasDetailLog: !!item.Status || (item.LogDetalhado?.length ?? 0) > 0,
        LogDetalhado: item.LogDetalhado,
        API: item.API,
        DataPesquisada: item.DataPesquisada,
        DataPesquisadaFim: item.DataPesquisadaFim,
        Empresa: item.Empresa,
        Inicio: item.Inicio,
        Fim: item.Fim,
        SolicitacaoEm: item.SolicitacaoEm,
        StatusProcessamento: item.StatusProcessamento,
        Status: item.Status,
        Usuario: item.Usuario,
        Arquivos: item.Arquivos,
        ArquivoId: item.ArquivoId,
        Curls: item.Curls
      })) || [];
    expandHistoryRows();
    integrationHistory.value
      ?.filter((row) => getRowSection(row) === "files")
      .forEach((row) => loadRowFiles(row));

    paginationRef.value.totalItems = response.Total ?? 0;
    paginationRef.value.totalPages = Math.ceil(
      paginationRef.value.totalItems / paginationRef.value.quantidadePorPagina
    );

    visibleDrawer.value = true;
  } catch (error) {
    console.error(error);
  } finally {
    isLoading.value = false;
  }
};

const handleClose = () => {
  visibleDrawer.value = false;
  integrationHistory.value = undefined;
  resetExpandState();
  emits("close");
};

const handlePagination = (page: number) => {
  paginationRef.value.page = page;
  getIntegrationHistory();
};

const handleRows = (rows: number) => {
  paginationRef.value.quantidadePorPagina = rows;
  getIntegrationHistory();
};

onMounted(async () => {
  await getIntegrationHistory();
});
</script>

<style scoped lang="scss">
.historico-integracao-drawer__content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 6px 0 0 0;
}

.historico-integracao-drawer__content-header {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 16px;
}

.historico-integracao-drawer__icon-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: #5d5d5d;
  cursor: pointer;
}

.historico-integracao-drawer__icon-button:hover {
  border-color: #d6ecff;
  background: #ffffff;
}

.historico-integracao-drawer__detalhe-bloco {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 32px 16px;
}

.historico-integracao-drawer__detalhe-titulo {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #0055ff;

  strong {
    color: #3d3d3d;
    font-size: 13px;
    line-height: 18px;
  }
}

.historico-integracao-drawer__detalhe-vazio {
  color: #5d5d5d;
  font-size: 13px;
  line-height: 18px;
}

.historico-integracao-drawer__file-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.historico-integracao-drawer__file-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 10px;
  border: 1px solid #d6ecff;
  border-radius: 6px;
  background: #ffffff;

  strong {
    color: #3d3d3d;
    font-size: 13px;
    line-height: 18px;
    overflow-wrap: anywhere;
  }

  span {
    color: #5d5d5d;
    font-size: 12px;
    line-height: 16px;
    overflow-wrap: anywhere;
  }
}

.historico-integracao-drawer__curl-list {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
  max-height: 240px;
  overflow-y: auto;
  border: 1px solid #d6ecff;
  border-radius: 6px;
  background: #ffffff;
}

.historico-integracao-drawer__curl-item {
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr) 28px;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;

  &:not(:last-child) {
    border-bottom: 1px solid #edf7ff;
  }
}

.historico-integracao-drawer__curl-index {
  color: #5d5d5d;
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
}

.historico-integracao-drawer__curl-text {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #3d3d3d;
  font-size: 12px;
  line-height: 18px;
  font-family: "Roboto Mono", "Courier New", monospace;
}

.historico-integracao-drawer__detalhes {
  width: 100%;
  height: auto;
  background-color: #edf7ff;
  padding-top: 16px;
}

.historico-integracao-drawer__status {
  margin: 0;
  color: #3d3d3d;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  padding: 0 32px 16px;
  white-space: pre-line;
}

.historico-integracao-drawer__log {
  display: block;
  margin: 0;
  color: #5d5d5d;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  padding: 0 32px 16px;
  white-space: pre-line;
}

:deep(.prisma-table__cell.prisma-table__expanded-cell),
:deep(.el-table__cell.el-table__expanded-cell) {
  padding: 0;
}
</style>
